import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Injector,
  input,
  Input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  signal,
  ViewChild,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import { FieldComponent } from '@appkit4/angular-components/field';
import { DatepickerOnDateInvalid } from '@shared/abstraction/datepicker/datepicker-on-date-invalid';
import { TextfieldValidation } from '@shared/abstraction/textfield-validation';
import { parseToTextfieldValidation } from '@shared/helpers/functions/parse-to-textfield-validation';
import moment from 'moment';
import { timer } from 'rxjs';

@Component({
  standalone: false,
  selector: 'gstar-datepicker',
  templateUrl: './datepicker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  private readonly injector = inject(Injector);

  label: InputSignal<string> = input<string>('');
  attachedDOM: InputSignal<string> = input<string>('');
  isRequired: InputSignal<boolean> = input<boolean>(false);
  ranged: InputSignal<boolean> = input<boolean>(false);
  disabled: ModelSignal<boolean> = model<boolean>(false);
  value: ModelSignal<Date[]> = model<Date[]>([]);

  @Input() set messages(errorMessages: JSONObject) {
    this.#errorMessages = parseToTextfieldValidation(
      errorMessages,
      this.label()
    );
  }

  @ViewChild('field', { static: false })
  field!: FieldComponent;

  @ViewChild('appkitInput', { static: false })
  appkitInput!: ElementRef;

  formControl!: FormControl;

  #errorMessages: TextfieldValidation[] = [];
  show = signal<boolean>(false);

  private onChange!: (value: Date[]) => void;
  private onTouched!: () => void;

  get errors(): string[] {
    return this.formControl.errors ? Object.keys(this.formControl.errors) : [];
  }

  get errorMessages(): TextfieldValidation | undefined {
    let errorMessage;
    if (this.formControl && this.formControl.errors) {
      Object.keys(this.formControl.errors).forEach((errorKey: string) => {
        errorMessage = this.#errorMessages.find(
          field => field.name === errorKey
        );
        if (errorMessage) {
          errorMessage.invalid =
            this.formControl.dirty || this.formControl.touched;
        }
      });
    }

    return errorMessage;
  }

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl);

    if (ngControl instanceof FormControlName) {
      this.formControl = this.injector
        .get(FormGroupDirective)
        .getControl(ngControl);
    } else {
      this.formControl = (ngControl as FormControlDirective)
        .form as FormControl;
    }
  }

  onSelectedDatesChange(dates: Date[]): void {
    if (
      (dates.length > 0 && !this.ranged()) ||
      (this.ranged() && dates.length === 2)
    ) {
      this.value.set(dates);
      this.onTouched();
      this.onChange(dates);
    }
  }

  dpOnDateInvalidFn(event: DatepickerOnDateInvalid): void {
    if (!event.value || event.invalid) {
      this.value.set([]);
      this.onTouched();
      this.onChange([]);
    }
  }

  inputChange(event: Event): void {
    const dateValue: string = this.appkitInput.nativeElement.value;
    if (event instanceof InputEvent) {
      if (!moment(dateValue, 'MM/DD/YYYY', true).isValid() && !event.data) {
        this.dpOnDateInvalidFn({ value: '', invalid: true });
      } else if (moment(dateValue, 'MM/DD/YYYY', true).isValid()) {
        this.dpOnDateInvalidFn({
          value: dateValue,
          invalid: false,
        });
      }
    }
  }

  writeValue(value: Date[]): void {
    if (value) {
      this.value.set(value);
      timer(0).subscribe(() => {
        (
          this.field as unknown as { cdr: ChangeDetectorRef }
        ).cdr.detectChanges();
      });
    } else {
      this.value.set([]);
    }
  }
  registerOnChange(fn: (value: Date[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  showHandler(): void {
    timer(100).subscribe(() => this.show.set(true));
  }

  closeHandler(): void {
    this.show.set(false);
  }

  @HostListener('window:click', ['$event'])
  clickOutside(event: Event): void {
    timer(0).subscribe(() => {
      if (this.field && this.show() && this.attachedDOM()) {
        this.field.closeDatepicker(event);
      }
    });
  }
}
